;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:tillamook-com":true});}catch(e){}}();import React from 'react';

import { BLOCKS } from 'components/ui/RichText';
import Text from 'components/ui/Text';

import { themeMapping } from './themeMapping';

import styles from './Lockup.module.scss';

export const getHeadlineRichTextOverrides = (
    theme,
    tag = 'h2',
    themeOverrides
) => {
    const Tag = tag;

    return {
        renderNode: {
            [BLOCKS.PARAGRAPH]: (_node, children) => {
                const themeConfig =
                    themeMapping[theme]?.headline ||
                    themeMapping['Large'].headline;

                return (
                    <Text
                        as={Tag}
                        className={styles.headline}
                        baseTheme={
                            themeOverrides?.baseTheme || themeConfig.baseTheme
                        }
                        themes={themeOverrides?.themes || themeConfig.themes}
                    >
                        {children}
                    </Text>
                );
            },
            [BLOCKS.HEADING_3]: (_node, children) => {
                const themeConfig =
                    themeMapping[theme]?.headline ||
                    themeMapping['Large'].headline;

                return (
                    <Text
                        as={Tag}
                        className={styles.headline}
                        baseTheme={
                            themeOverrides?.baseTheme || themeConfig.baseTheme
                        }
                        themes={themeOverrides?.themes || themeConfig.themes}
                    >
                        {children}
                    </Text>
                );
            },
        },
        renderText: text => {
            // Convert newline characters to <br /> tags
            return text.split('\n').map((line, index, array) => (
                <React.Fragment key={index}>
                    {line}
                    {index < array.length - 1 && <br />}
                </React.Fragment>
            ));
        },
    };
};

export const getCopyRichTextOverrides = (theme, tag = 'p', themeOverrides) => {
    const Tag = tag;

    let paragraphs = [];

    return {
        renderNode: {
            [BLOCKS.PARAGRAPH]: (node, children) => {
                paragraphs.push(node);

                // check if this is the last paragraph and if it's empty
                const isLastParagraph =
                    paragraphs[paragraphs.length - 1] === node;
                const isEmpty = React.Children.toArray(children).every(
                    child => typeof child === 'string' && child.trim() === ''
                );

                // skip rendering if it's the last paragraph and it's empty
                if (isLastParagraph && isEmpty) {
                    return null;
                }

                const themeConfig =
                    themeMapping[theme]?.copy || themeMapping['Large'].copy;

                return (
                    <Text
                        as={Tag}
                        className={styles.paragraph}
                        baseTheme={
                            themeOverrides?.baseTheme || themeConfig.baseTheme
                        }
                        themes={themeOverrides?.themes || themeConfig.themes}
                    >
                        {children}
                    </Text>
                );
            },
        },
        renderText: undefined,
    };
};

export const isRichText = content => {
    return (
        typeof content === 'object' &&
        content !== null &&
        'json' in content &&
        !Array.isArray(content)
    );
};

export const isValidContent = content => {
    return (
        typeof content === 'string' ||
        (typeof content === 'object' &&
            content !== null &&
            !Array.isArray(content) &&
            'json' in content)
    );
};

// Helper to normalize rich text content to a consistent structure
const getRichTextContent = richTextContent => {
    if (!richTextContent) return null;

    // Check if it's the json property from GraphQL (most common case)
    if (richTextContent.json && richTextContent.json.content) {
        return richTextContent.json.content;
    }
    // Check if it's already the content array
    if (Array.isArray(richTextContent.content)) {
        return richTextContent.content;
    }
    // Check if it's a direct content array
    if (Array.isArray(richTextContent)) {
        return richTextContent;
    }

    return null;
};

// Function to extract text from rich text content
export const extractTextFromRichText = richTextContent => {
    const content = getRichTextContent(richTextContent);
    if (!content || !Array.isArray(content)) return '';

    const extractText = nodes => {
        return nodes
            .map(node => {
                if (!node) return '';

                // Handle paragraph nodes and other block nodes
                if (
                    node.nodeType &&
                    node.content &&
                    Array.isArray(node.content)
                ) {
                    return extractText(node.content);
                }

                // Handle text nodes
                if (node.nodeType === 'text' && node.value) {
                    return node.value;
                }

                return '';
            })
            .filter(text => text.trim() !== '') // Remove empty strings
            .join(' ');
    };

    return extractText(content);
};

// Function to check if any word exceeds the threshold
export const hasLongWords = (text, threshold) => {
    if (!text || typeof text !== 'string') return false;

    const words = text.split(/\s+/);
    return words.some(word => {
        // Remove punctuation and check word length
        const cleanWord = word.replace(/[^\w]/g, '');
        return cleanWord.length > threshold;
    });
};

// Function to check for long words in headline content
export const checkForLongWords = (headlineContent, isRichText, threshold) => {
    if (!headlineContent) return false;

    if (isRichText) {
        const extractedText = extractTextFromRichText(headlineContent);
        return hasLongWords(extractedText, threshold);
    } else {
        return hasLongWords(headlineContent, threshold);
    }
};

// Helper to check if a node is a text block (paragraph or heading)
const isTextBlockNode = node => {
    return (
        node.nodeType === 'paragraph' ||
        node.nodeType === 'heading-1' ||
        node.nodeType === 'heading-2' ||
        node.nodeType === 'heading-3' ||
        node.nodeType === 'heading-4' ||
        node.nodeType === 'heading-5' ||
        node.nodeType === 'heading-6'
    );
};

// Function to convert hard breaks (multiple paragraphs) in rich text to <br /> tags
export const convertHardBreaksToLineBreaks = richTextContent => {
    const content = getRichTextContent(richTextContent);
    if (!content || !Array.isArray(content)) {
        return richTextContent;
    }

    // Filter out only paragraph and heading nodes
    const textNodes = content.filter(isTextBlockNode);

    // If there's only one node, no conversion needed
    if (textNodes.length <= 1) {
        return richTextContent;
    }

    // Combine all text nodes into a single paragraph with line breaks
    const combinedContent = [];

    textNodes.forEach((node, index) => {
        // Add the content of the current node
        if (node.content && Array.isArray(node.content)) {
            combinedContent.push(...node.content);
        }

        // Add a line break after each node except the last one
        if (index < textNodes.length - 1) {
            combinedContent.push({
                nodeType: 'text',
                value: '\n',
                marks: [],
                data: {},
            });
        }
    });

    // Create a new document with a single paragraph
    const newDoc = {
        ...richTextContent.json,
        content: [
            {
                nodeType: 'paragraph',
                data: {},
                content: combinedContent,
            },
        ],
    };

    // Return the modified rich text object
    return {
        ...richTextContent,
        json: newDoc,
    };
};
